import {
  RequestBuilder
} from "./chunk-CO4RI5BX.mjs";
import {
  validateAuthCodeVerifierKey,
  validateConfig,
  validateCsrf,
  validateNonce,
  validateResponseKeys,
  validateResponseKeysWithoutRT,
  validateURLSearchParams
} from "./chunk-5YZBVQLS.mjs";
import {
  AuthCodeNotFoundError,
  CsrfNotFoundError,
  JwtValidationError
} from "./chunk-RDVYLKWU.mjs";
import {
  errorMessages
} from "./chunk-KSIEOKOR.mjs";
import {
  getApiUrl,
  getAudienceUrl,
  getBaseUrl,
  getLoginUrl,
  getMyAccountUrl
} from "./chunk-XEDCQDS7.mjs";
import {
  __decorateClass
} from "./chunk-AYNJNFEN.mjs";

// src/services/auth.ts
import { encode } from "qss";
import { Base64 } from "js-base64";
import { generate } from "randomized-string";
import { Mutex } from "async-mutex";
import {
  createCodeVerifier as createVerifier,
  createCodeChallenge as createChallenge,
  generateRandomString,
  verifyJwt
} from "@inner-source/ciam-sdk";
var PcidAuth = class {
  constructor(config) {
    /** Key to store nonce in session storage */
    this.nonceKey = "pcidNonceKey";
    /** Key to store csrf in session storage */
    this.csrf = "csrfKey";
    /** Key to store code verifier in session storage
     * Code verifier is used to protect against man in the middle attacks for
     * applications that don't have a server side to protect secrets.
     */
    this.codeVerifierKey = "pcidCodeVerifierKey";
    this.requestBuilder = new RequestBuilder();
    this.correlationId = generate(10);
    // Mutex for single-flight refresh
    this.refreshMutex = new Mutex();
    this.refreshingPromise = null;
    config.idcsBaseUrl = getAudienceUrl(config.environment);
    config.ciamApiUrl = getBaseUrl(config.environment);
    config.managementUrl = getMyAccountUrl(config.environment);
    this.config = config;
  }
  basicAuth() {
    const token = Base64.encode(
      `${this.config.clientId}:${this.config.clientSecret}`
    );
    return `Basic ${token}`;
  }
  clearNonce() {
    sessionStorage.removeItem(this.nonceKey);
  }
  setNonce(nonce) {
    sessionStorage.setItem(this.nonceKey, nonce);
  }
  getNonce() {
    const ciamAuthNonce = sessionStorage.getItem(this.nonceKey);
    if (ciamAuthNonce === null) {
      void this.log("ERROR" /* ERROR */, "Nonce not found in sessionStorage.");
      throw new Error("Nonce not found in sessionStorage.");
    }
    return ciamAuthNonce;
  }
  clearCsrf() {
    sessionStorage.removeItem(this.csrf);
  }
  setCsrf(csrf) {
    sessionStorage.setItem(this.csrf, csrf);
  }
  setLanguage(language) {
    this.config.stateParams.language = language;
  }
  setRedirectUrl(redirectUrl) {
    this.config.redirectUrl = redirectUrl;
  }
  getCsrf() {
    const ciamAuthCsrf = sessionStorage.getItem(this.csrf);
    if (ciamAuthCsrf === null) {
      throw new CsrfNotFoundError("Csrf not found in sessionStorage.");
    }
    return ciamAuthCsrf;
  }
  clearCodeVerifier() {
    sessionStorage.removeItem(this.codeVerifierKey);
  }
  setCodeVerifier(codeVerifier) {
    sessionStorage.setItem(this.codeVerifierKey, codeVerifier);
  }
  getCodeVerifier() {
    const authCodeVerifierKey = sessionStorage.getItem(this.codeVerifierKey);
    this.clearCodeVerifier();
    return authCodeVerifierKey;
  }
  generateTokenUrl() {
    return `${getLoginUrl(this.config.environment)}${"/oauth2/v1/token" /* ServerTokenPath */}`;
  }
  /** Returns client Basic auth header and 'Content-Type' headers */
  getHeaders() {
    const headers = {};
    headers.Authorization = this.basicAuth();
    headers["Content-Type"] = "application/x-www-form-urlencoded;charset=UTF-8";
    return headers;
  }
  getAuthTokenFromCode(data) {
    const body = encode({
      grant_type: "authorization_code" /* GrantType */,
      code: data.queryArgs.get("code" /* Code */),
      redirect_uri: this.config.redirectUrl,
      code_verifier: data.codeVerifier,
      client_id: this.config.clientId
    });
    return this.fetchTokens(body);
  }
  fetchTokens(data) {
    return this.requestBuilder.post(this.generateTokenUrl(), data, {
      ["headers" /* Headers */]: this.getHeaders()
    }).then((tokenResponse) => {
      if (this.config.addOfflineAccessToScopes) {
        this.getFetchTokenResponseWithRT(tokenResponse);
      } else {
        this.getFetchTokenResponseWithoutRT(tokenResponse);
      }
      return tokenResponse;
    }).then((tokenResponse) => {
      if (this.config.pkceFlow) {
        this.validateNonceHelper(tokenResponse);
      }
      return tokenResponse;
    }).then(
      (tokenResponse) => this.cleanUp(tokenResponse)
    ).catch((error) => {
      void this.log(
        "ERROR" /* ERROR */,
        `Error while fetching tokens: ${error}`
      );
      throw error;
    });
  }
  getFetchTokenResponseWithRT(data) {
    return data;
  }
  getFetchTokenResponseWithoutRT(data) {
    return data;
  }
  validateNonceHelper(data) {
    return data;
  }
  cleanUp(tokenResponse) {
    this.clearNonce();
    this.clearCodeVerifier();
    this.clearCsrf();
    return tokenResponse;
  }
  /**
   * Refreshes the access token using refresh token
   *
   * sting refreshToken - The user's refresh token.
   * @returns Promise<PostFetchTokenResponseType> - A new access token and a refresh token.
   */
  async refreshAccessToken(refreshToken) {
    const body = {
      grant_type: "refresh_token",
      refresh_token: refreshToken
    };
    const tokens = await this.fetchRefreshTokens(body);
    const validateJwt = await this.validateToken(tokens.access_token);
    if (!validateJwt) {
      void this.log(
        "ERROR" /* ERROR */,
        `Error while validating refreshed access token`
      );
      throw new JwtValidationError(errorMessages.JWT_VALIDATION_FAILED);
    }
    return tokens;
  }
  fetchRefreshTokens(data) {
    return this.requestBuilder.post(this.generateTokenUrl(), data, {
      ["headers" /* Headers */]: this.getHeaders()
    }).then(
      (tokenResponse) => this.getFetchRefreshTokenResponse(tokenResponse)
    ).catch((error) => {
      const errorDescription = error.response?.data?.error_description || "Unknown error";
      void this.log(
        "ERROR" /* ERROR */,
        `Error while refreshing access token: ${errorDescription}`
      );
      throw error;
    });
  }
  getFetchRefreshTokenResponse(data) {
    void this.log("INFO" /* INFO */, "Access Token refreshed");
    return data;
  }
  getQueryArgs(search) {
    return search;
  }
  /**
   * Handles the sign-in process asynchronously.
   *
   * @returns Promise<boolean> - A Promise that resolves to a boolean indicating the success of the sign-in process.
   * @throws JwtValidationError - error if AT returned is invalid.
   */
  async handleSignIn(code, state) {
    const search = new URLSearchParams();
    search.append("code", code);
    search.append("state", state);
    void this.log(
      "INFO" /* INFO */,
      `Handle sign in url params: ${search}`
    );
    if (code && state) {
      try {
        const queryArgs = this.getQueryArgs(search);
        let tokens;
        if (this.config.pkceFlow) {
          const codeVerifier = this.getCodeVerifier();
          tokens = await this.getAuthTokenFromCode({ queryArgs, codeVerifier });
        } else {
          tokens = await this.getAuthTokenFromCode({ queryArgs });
        }
        const validateJwt = await this.validateToken(tokens.access_token);
        if (!validateJwt) {
          void this.log("ERROR" /* ERROR */, `Error while validating access token`);
          throw new JwtValidationError(errorMessages.JWT_VALIDATION_FAILED);
        }
        void this.log("INFO" /* INFO */, "Tokens returned.");
        return tokens;
      } catch (error) {
        if (!(error instanceof CsrfNotFoundError || error instanceof AuthCodeNotFoundError)) {
          void this.log(
            "ERROR" /* ERROR */,
            `Error while fetching tokens: ${error}`
          );
        }
        throw error;
      }
    }
    return null;
  }
  /**
   * Generates an object for use in the authentication process.
   *
   * @returns Object - The generated authentication object.
   */
  generateStateObj() {
    const obj = {
      csrf: generateRandomString(),
      relyingParty: this.config.stateParams.relyingParty,
      intent: this.config.stateParams.intent,
      language: this.config.stateParams.language,
      scope: this.generateScope(),
      keepMeSignedIn: this.config.keepMeSignedIn,
      showIsThisYouPrompt: this.config.showIsThisYouPrompt,
      // clientId is required to store values in local storage
      clientId: this.config.clientId,
      customProps: this.config.stateParams.customProps,
      utm_source: this.config.utm?.utm_source,
      utm_medium: this.config.utm?.utm_medium,
      utm_campaign: this.config.utm?.utm_campaign,
      utm_term: this.config.utm?.utm_term,
      utm_content: this.config.utm?.utm_content
    };
    this.setCsrf(obj.csrf);
    return Buffer.from(JSON.stringify(obj)).toString("base64");
  }
  /**
   * Generates the scope for the authentication process.
   *
   * @returns string - The generated scope string.
   */
  generateScope() {
    const offlineAccess = this.config.addOfflineAccessToScopes ? " offline_access" : "";
    return `openid ${this.config.scope}${offlineAccess}`;
  }
  /**
   * Generates a login URL for the authentication process.
   *
   * @returns string - The generated PCID login URL with all necessary stateParams and redirects to create-account page.
   */
  async pcidCreateAccountFunction() {
    this.config.stateParams.intent = "register";
    return this.generateLoginUrl();
  }
  /**
   * Generates a login URL for the authentication process.
   *
   * @returns string - The generated PCID login URL with all necessary stateParams and redirects to login page.
   */
  async pcidLoginFunction() {
    this.config.stateParams.intent = "login";
    return this.generateLoginUrl();
  }
  /**
   * Generates a login URL for the authentication process.
   *
   * @returns string - The generated PCID login URL with all necessary stateParams.
   */
  async generateLoginUrl() {
    const stateObj = this.generateStateObj();
    let urlArgs;
    if (this.config.pkceFlow) {
      const codeChallenge = await this.getCodeChallenge();
      this.generateNonce();
      urlArgs = decodeURIComponent(
        this.generateUrlArgs(this.getNonce(), stateObj, codeChallenge)
      );
    } else {
      urlArgs = decodeURIComponent(this.generateUrlArgs("", stateObj, ""));
    }
    return this.generateAuthenticatedLoginUrl(urlArgs);
  }
  async getCodeChallenge() {
    const codeChallenge = await createChallenge(this.generateCodeVerifier());
    return codeChallenge;
  }
  generateCodeVerifier() {
    const codeVerifier = createVerifier();
    this.setCodeVerifier(codeVerifier);
    return codeVerifier;
  }
  generateNonce() {
    const nonce = generateRandomString();
    this.setNonce(nonce);
  }
  /**
   * sets custom props
   * @param prop- new custom property to be added.
   */
  setCustomProps(prop) {
    const existingProps = this.config.stateParams.customProps;
    const mergedProps = {
      ...existingProps,
      ...prop
    };
    this.config.stateParams.customProps = mergedProps;
  }
  /**
   * Generates URL arguments for the authentication process.
   *
   *  string nonce - The nonce value for security.
   *  string stateObj - The authentication state object.
   *  Promise<string> codeChallenge - The promise for the code challenge.
   * @returns Object - The generated URL arguments.
   */
  generateUrlArgs(nonce, stateObj, codeChallenge) {
    if (!this.config.pkceFlow) {
      return encode({
        client_id: this.config.clientId,
        response_type: "code" /* Code */,
        redirect_uri: this.config.redirectUrl,
        scope: this.generateScope(),
        state: stateObj
      });
    }
    return encode({
      client_id: this.config.clientId,
      response_type: "code" /* Code */,
      redirect_uri: this.config.redirectUrl,
      code_challenge: codeChallenge,
      code_challenge_method: "S256" /* CodeChallengeMethod */,
      scope: this.generateScope(),
      state: stateObj,
      nonce
    });
  }
  /**
   * Generates an authenticated login URL based on the provided URL arguments.
   * string urlArgs - The URL arguments for the authentication process.
   * @returns string - The authenticated login URL.
   */
  generateAuthenticatedLoginUrl(urlArgs) {
    const urlPath = `${getLoginUrl(this.config.environment)}${"/oauth2/v1/authorize" /* AuthorizePath */}`;
    const authenticatedUrl = `${urlPath}?${urlArgs}`;
    void this.log("INFO" /* INFO */, `Login url generated: ${authenticatedUrl}`);
    return authenticatedUrl;
  }
  /**
   * Validates AT issued by IDCS.
   * @param accessToken - access tokens issues by Oracle Idcs.
   */
  async validateToken(accessToken) {
    const jwkUrl = `${getLoginUrl(
      this.config.environment
    )}/admin/v1/SigningCert/jwk`;
    const validJwt = await verifyJwt(jwkUrl, accessToken).catch((error) => {
      void this.log("ERROR" /* ERROR */, `Error while jwk validation call ${error}`);
    });
    return validJwt;
  }
  /**
   * Prints log in the client-logging service.
   * @param logLevel - logging level INFO ERROR etc.
   * @param logMsg - log messages.
   * */
  /* eslint-disable @typescript-eslint/require-await -- Logs must be Async to prevent blocking */
  async log(logLevel, logMsg) {
    try {
      const logRequest = {
        payload: logMsg,
        "logging-level": logLevel
      };
      const loggingUrl = `${getApiUrl(this.config.environment)}/v1/client/logs`;
      fetch(loggingUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "relying-party": this.config.stateParams.relyingParty,
          "x-forwarded-for": this.config.logDetails?.sourceIps || "",
          source: "WEB",
          "user-agent": this.config.logDetails?.userAgent || "",
          "x-correlation-id": this.correlationId
        },
        body: JSON.stringify(logRequest)
      }).catch((_error) => {
      });
    } catch (error) {
      void this.log("ERROR" /* ERROR */, `Error creating a log: ${error}`);
    }
  }
  /**
   * Refreshes the access token using refresh token with mutex protection
   * This is a drop-in replacement for the existing refreshAccessToken method
   * that adds single-flight refresh protection
   *
   * @param refreshToken - The user's refresh token
   * @returns Promise<PostFetchTokenResponseType> - A new access token and refresh token
   */
  async refreshAccessTokenWithMutex(refreshToken) {
    if (this.refreshingPromise) {
      void this.log(
        "INFO" /* INFO */,
        "refreshAccessTokenWithMutex: Refresh in progress, awaiting existing promise"
      );
      return this.refreshingPromise;
    }
    this.refreshingPromise = this.refreshMutex.runExclusive(async () => {
      void this.log(
        "INFO" /* INFO */,
        "refreshAccessTokenWithMutex: Starting actual refresh"
      );
      const tokens = await this.refreshAccessToken(refreshToken);
      void this.log(
        "INFO" /* INFO */,
        "refreshAccessTokenWithMutex: Refresh completed"
      );
      return tokens;
    });
    try {
      const result = await this.refreshingPromise;
      return result;
    } finally {
      this.refreshingPromise = null;
    }
  }
};
__decorateClass([
  validateAuthCodeVerifierKey
], PcidAuth.prototype, "getCodeVerifier", 1);
__decorateClass([
  validateResponseKeys
], PcidAuth.prototype, "getFetchTokenResponseWithRT", 1);
__decorateClass([
  validateResponseKeysWithoutRT
], PcidAuth.prototype, "getFetchTokenResponseWithoutRT", 1);
__decorateClass([
  validateNonce
], PcidAuth.prototype, "validateNonceHelper", 1);
__decorateClass([
  validateResponseKeys
], PcidAuth.prototype, "getFetchRefreshTokenResponse", 1);
__decorateClass([
  validateURLSearchParams,
  validateCsrf
], PcidAuth.prototype, "getQueryArgs", 1);
PcidAuth = __decorateClass([
  validateConfig
], PcidAuth);

export {
  PcidAuth
};
